"use client";

import type { CSSProperties } from "react";

import { BRAND_COLORS, brandGradientCss } from "@tami/tokens";
import { cn } from "../../cn";

export interface BioCtaPosterProps {
  readonly href: string;
  readonly label: string;
  /** Linha de apoio abaixo do título, ex.: "Grátis para começar". */
  readonly note: string;
  readonly className?: string;
}

/**
 * O pôster escuro do CTA principal da página `/links` (a da bio do
 * Instagram). Na direção escolhida (Task 6) o gradiente da marca aparece uma
 * única vez na página inteira, confinado a uma tarja fina no topo do pôster —
 * não como fundo do cartão nem da página. É por isso que só este componente
 * chama `brandGradientCss`; nenhum outro lugar da tela deveria repetir isso.
 *
 * Fundo escuro por padrão (não branco/creme) porque a hierarquia da direção é
 * por contraste de escala e de tom, não por cor: o CTA precisa ser o bloco
 * mais escuro da página para ler como o mais importante.
 */
export function BioCtaPoster({ href, label, note, className }: BioCtaPosterProps) {
  return (
    <a
      href={href}
      className={cn(
        "group block overflow-hidden rounded-[20px] no-underline transition-transform duration-150",
        "hover:-translate-y-0.5",
        // O anel de foco segue o raio do pôster (20px), não os 6px do HTML de
        // referência: lá o valor vem de uma regra genérica de link (herança,
        // não escolha). Um anel quase reto por cima de um cartão arredondado
        // ficaria pior do que acompanhar a curva do próprio pôster.
        "focus-visible:rounded-[20px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[--biocta-focus]",
        "motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        className,
      )}
      style={
        {
          backgroundColor: BRAND_COLORS.ink,
          color: BRAND_COLORS.white,
          "--biocta-focus": BRAND_COLORS.offWhite,
        } as CSSProperties
      }
    >
      <span
        aria-hidden
        className="block h-2.5 w-full"
        style={{ backgroundImage: brandGradientCss(90) }}
      />
      <span className="block px-6 pb-6 pt-[22px]">
        <span className="block text-[30px] font-bold leading-[1.08] tracking-[-0.3px]">
          {label}
          <span aria-hidden className="ml-2">
            →
          </span>
        </span>
        <span
          className="mt-2.5 block text-sm font-semibold tracking-[0.2px]"
          style={{ color: BRAND_COLORS.green }}
        >
          {note}
        </span>
      </span>
    </a>
  );
}
